import { useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { appointmentSlotsService, FetchAppointmentsParams } from '@/services/features/appointmentSlotsService.ts';
import { useAppointmentSlotsState } from './useAppointmentSlotsState.ts';

export const useAppointmentSlotsOperations = () => {
  const { hasuraToken, user, token } = useAuth();
  const { toast } = useToast();
  const state = useAppointmentSlotsState();

  // Fetch clinics
  const fetchClinics = useCallback(async () => {
    if (!hasuraToken) {
      return;
    }

    state.setIsLoading(true);
    try {
      const clinicsData = await appointmentSlotsService.fetchClinics(hasuraToken);
      state.setClinics(clinicsData);
    } catch (error) {
      console.error("There was an error fetching clinics!", error);
      toast({
        title: "Error",
        description: "There was an error fetching clinics!",
        variant: "destructive",
      });
    } finally {
      state.setIsLoading(false);
    }
  }, [hasuraToken, toast, state.setIsLoading, state.setClinics]); // Only include the specific setters needed

  // Fetch total count of appointments
  const fetchAppointmentsCount = useCallback(async (overrideSelectedClinicId?: number | null) => {
    if (!hasuraToken) {
      return 0;
    }

    try {
      const params: FetchAppointmentsParams = {
        selectedClinicId: overrideSelectedClinicId !== undefined ? overrideSelectedClinicId : state.selectedClinicId,
        selectedFacility: state.selectedFacility || undefined,
        facilities: state.facilities.length > 0 ? state.facilities : undefined,
        filterStartDate: state.filterStartDate,
        filterEndDate: state.filterEndDate,
        rowsPerPage: state.rowsPerPage,
        offset: 0,
        useAdvancedFiltering: false
      };

      const count = await appointmentSlotsService.fetchAppointmentsCount(hasuraToken, params);
      state.setTotalCount(count);
      return count;
    } catch (error) {
      console.error("Error fetching appointments count:", error);
      return 0;
    }
  }, [hasuraToken, state.selectedClinicId, state.selectedFacility, state.facilities, state.filterStartDate, state.filterEndDate, state.rowsPerPage, state.setTotalCount]);

  // Fetch appointments with enhanced pagination support
  const fetchAppointments = useCallback(async (newOffset = 0, reset = false, useAdvancedFiltering = false, overrideSelectedClinicId?: number | null) => {
    if (!hasuraToken) {
      return;
    }

    state.setIsLoading(true);
    try {
      // First, fetch the total count if this is a reset (new search/filter)
      if (reset) {
        await fetchAppointmentsCount(overrideSelectedClinicId);
      }

      const params: FetchAppointmentsParams = {
        selectedClinicId: overrideSelectedClinicId !== undefined ? overrideSelectedClinicId : state.selectedClinicId,
        selectedFacility: state.selectedFacility || undefined,
        facilities: state.facilities.length > 0 ? state.facilities : undefined,
        filterStartDate: useAdvancedFiltering ? state.filterStartDate : undefined,
        filterEndDate: useAdvancedFiltering ? state.filterEndDate : undefined,
        // Don't send time filters to API - they will be applied locally
        filterStartTime: undefined,
        filterEndTime: undefined,
        rowsPerPage: state.rowsPerPage,
        offset: newOffset,
        useAdvancedFiltering
      };

      let newAppointments = await appointmentSlotsService.fetchAppointments(hasuraToken, params);
      
      // Helper: convert various time string formats to minutes since midnight
      const toMinutes = (input?: string): number | undefined => {
        if (!input) return undefined;
        const s = input.trim();
        // Match 12-hour formats like "9:05 AM" or with seconds "09:05:00 PM"
        const ampmMatch = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
        if (ampmMatch) {
          let hh = parseInt(ampmMatch[1], 10);
          const mm = parseInt(ampmMatch[2], 10);
          const mer = ampmMatch[3].toUpperCase();
          if (mer === 'AM') hh = hh % 12; // 12 AM -> 0
          else hh = (hh % 12) + 12;       // 12 PM -> 12
          return hh * 60 + mm;
        }
        // Match 24-hour formats like "09:05" or "09:05:00"
        const parts = s.split(':');
        if (parts.length >= 2) {
          const hh = parseInt(parts[0], 10);
          const mm = parseInt(parts[1], 10);
          if (!Number.isNaN(hh) && !Number.isNaN(mm)) return hh * 60 + mm;
        }
        return undefined;
      };
      
      // Apply local time filtering if advanced filtering is used and time filters are set
      if (useAdvancedFiltering && (state.filterStartTime || state.filterEndTime)) {
        const startFilterMin = state.filterStartTime ? toMinutes(state.filterStartTime) : undefined;
        const endFilterMin = state.filterEndTime ? toMinutes(state.filterEndTime) : undefined;
        
        newAppointments = newAppointments.filter(appointment => {
          const startMin = toMinutes(appointment.appointment_start_time);
          if (startMin === undefined) return false;
          if (startFilterMin !== undefined && startMin < startFilterMin) return false;
          if (endFilterMin !== undefined && startMin > endFilterMin) return false;
          return true;
        });
      }

      // Sort appointments by date descending (latest first), then by start time descending
      newAppointments = newAppointments.sort((a, b) => {
        const dateA = a.appointment_date || '';
        const dateB = b.appointment_date || '';
        const dateComparison = dateB.localeCompare(dateA);
        
        if (dateComparison !== 0) {
          return dateComparison;
        }
        
        const timeA = toMinutes(a.appointment_start_time) ?? 0;
        const timeB = toMinutes(b.appointment_start_time) ?? 0;
        return timeB - timeA;
      });
      
      if (reset || newOffset === 0) {
        // Reset appointments for new search or initial load
        state.setAppointments(newAppointments);
        
        // Store original data only when doing a fresh fetch without filters
        if (!useAdvancedFiltering) {
          state.setOriginalAppointments(newAppointments);
        }
      } else {
        // Append new appointments for pagination
        state.setAppointments(prev => [...prev, ...newAppointments]);
      }
      
      // Check if there's more data based on current offset and total count
      state.setHasMoreData(newOffset + newAppointments.length < state.totalCount);
      state.setOffset(newOffset);
    } catch (error) {
      console.error("There was an error fetching appointments!", error);
      toast({
        title: "Error",
        description: "Failed to fetch appointments",
        variant: "destructive",
      });
    } finally {
      state.setIsLoading(false);
    }
  }, [hasuraToken, toast, fetchAppointmentsCount, state.selectedClinicId, state.selectedFacility, state.facilities, state.filterStartDate, state.filterEndDate, state.filterStartTime, state.filterEndTime, state.rowsPerPage, state.totalCount, state.setIsLoading, state.setAppointments, state.setOriginalAppointments, state.setHasMoreData, state.setOffset]);

  // Fetch facilities - handles both single clinic and all clinics
  const fetchFacilities = useCallback(async (clinicId: number | null, isAllClinics = false) => {
    if (!hasuraToken) {
      state.setFacilities([]);
      return;
    }

    // If no clinic selected and not "all clinics", clear facilities
    if (!clinicId && !isAllClinics) {
      state.setFacilities([]);
      return;
    }

    state.setIsLoadingFacilities(true);
    try {
      let facilitiesData = [];

      if (isAllClinics) {
        // Use efficient single API call to fetch facilities from all clinics
        const allClinicIds = state.clinics.map(clinic => clinic.clinic_id);
        facilitiesData = await appointmentSlotsService.fetchFacilitiesForAllClinics(hasuraToken, allClinicIds);
      } else if (clinicId) {
        // Fetch facilities for specific clinic
        facilitiesData = await appointmentSlotsService.fetchFacilities(hasuraToken, clinicId);
      }

      state.setFacilities(facilitiesData);
    } catch (error) {
      console.error("There was an error fetching facilities!", error);
      state.setFacilities([]);
      toast({
        title: "Error",
        description: "Failed to fetch facilities",
        variant: "destructive",
      });
    } finally {
      state.setIsLoadingFacilities(false);
    }
  }, [hasuraToken, toast, state.clinics, state.setFacilities, state.setIsLoadingFacilities]); // Only include the specific state values and setters needed

  // Fetch dialog facilities
  const fetchDialogFacilities = useCallback(async (clinicId: number | null) => {
    if (!hasuraToken || !clinicId) {
      state.setDialogFacilities([]);
      return;
    }

    state.setIsLoadingDialogFacilities(true);
    try {
      const facilitiesData = await appointmentSlotsService.fetchFacilities(hasuraToken, clinicId);
      state.setDialogFacilities(facilitiesData);
    } catch (error) {
      console.error("There was an error fetching dialog facilities!", error);
      state.setDialogFacilities([]);
    } finally {
      state.setIsLoadingDialogFacilities(false);
    }
  }, [hasuraToken, state.setDialogFacilities, state.setIsLoadingDialogFacilities]); // Only include the specific setters needed

  // Handle clinic change - implements sequential filtering workflow
  const handleClinicChange = useCallback((clinicName: string) => {
    // Set display name properly for "All Clinics"
    const displayName = clinicName === "all" ? "All Clinics" : clinicName;
    state.setSelectedClinic(displayName);
    state.setSelectedFacility(""); // Reset facility selection
    state.setCurrentPage(1); // Reset pagination when clinic changes

    let nextClinicId: number | null = null;

    if (clinicName === "all" || clinicName === "All Clinics" || !clinicName) {
      state.setSelectedClinicId(null);
      // Fetch facilities from ALL clinics when "All Clinics" is selected
      fetchFacilities(null, true);
      nextClinicId = null;
    } else {
      const clinic = state.clinics.find(c => c.clinic_name === clinicName);
      if (clinic) {
        state.setSelectedClinicId(clinic.clinic_id);
        // Fetch facilities for the specific clinic
        fetchFacilities(clinic.clinic_id, false);
        nextClinicId = clinic.clinic_id;
      } else {
        state.setSelectedClinicId(null);
        state.setFacilities([]);
        nextClinicId = null;
      }
    }

    // Fetch appointments with the explicit clinic ID override to avoid stale state
    fetchAppointments(0, true, false, nextClinicId);
  }, [fetchFacilities, fetchAppointments, state.clinics, state.setSelectedClinic, state.setSelectedFacility, state.setCurrentPage, state.setSelectedClinicId, state.setFacilities]);

  // Handle facility change - trigger API call with facility filter
  const handleFacilityChange = useCallback((facilityName: string) => {
    state.setSelectedFacility(facilityName);
    state.setCurrentPage(1); // Reset pagination when facility changes
    
    // Trigger API call with facility filter
    fetchAppointments(0, true, false);
  }, [state, fetchAppointments]);


  // Handle dialog clinic change
  const handleDialogClinicChange = useCallback((clinicName: string) => {
    state.setDialogClinic(clinicName);
    state.setSelectedDialogFacility("");
    state.setDialogFacilities([]);
    const clinic = state.clinics.find(c => c.clinic_name === clinicName);
    if (clinic) {
      state.setDialogClinicId(clinic.clinic_id);
    } else {
      state.setDialogClinicId(null);
    }
  }, [state.clinics, state.setDialogClinic, state.setSelectedDialogFacility, state.setDialogFacilities, state.setDialogClinicId]);

  // Handle pagination (server-side pagination - fetch new pages from backend)
  const handleRowsPerPageChange = useCallback((value: string) => {
    state.setRowsPerPage(parseInt(value));
    state.setCurrentPage(1); // Reset to first page when changing rows per page
    state.setOffset(0);
    // Refetch with new page size
    fetchAppointments(0, true, state.isFilterApplied);
  }, [state.setRowsPerPage, state.setCurrentPage, state.setOffset, fetchAppointments, state.isFilterApplied]);

  const handlePageChange = useCallback((page: number) => {
    const newOffset = (page - 1) * state.rowsPerPage;
    state.setCurrentPage(page);
    state.setOffset(newOffset);
    // Fetch data for the new page
    fetchAppointments(newOffset, false, state.isFilterApplied);
  }, [state.rowsPerPage, state.setCurrentPage, state.setOffset, fetchAppointments, state.isFilterApplied]);

  const handleNextPage = useCallback(() => {
    const totalPages = Math.ceil(state.totalCount / state.rowsPerPage);
    const nextPage = state.currentPage + 1;
    
    if (nextPage <= totalPages) {
      handlePageChange(nextPage);
    }
  }, [state.totalCount, state.rowsPerPage, state.currentPage, handlePageChange]);

  const handlePrevPage = useCallback(() => {
    if (state.currentPage > 1) {
      handlePageChange(state.currentPage - 1);
    }
  }, [state.currentPage, handlePageChange]);

  // Handle search functionality
  const handleSearch = useCallback(() => {
    // Reset pagination and fetch appointments with current search/filter criteria
    state.setCurrentPage(1);
    state.setOffset(0);
    fetchAppointments(0, true);

    toast({
      title: "Search Applied",
      description: "Search results updated successfully!",
    });
  }, [state.setCurrentPage, state.setOffset, fetchAppointments, toast]);

  // Handle refresh data - refetch appointments while maintaining current filters
  const handleRefreshData = useCallback(() => {
    // Refetch appointments with current filter state maintained
    fetchAppointments(0, true, state.isFilterApplied);

    toast({
      title: "Refreshed",
      description: "Data has been refreshed successfully!",
    });
  }, [fetchAppointments, state.isFilterApplied, toast]);

  // Handle reload - fetch all slots and reset filters
  const handleReload = useCallback(() => {
    // Reset all selections and search queries
    state.resetFilterState();

    // Refetch data from all clinics (no clinic filter)
    state.setSelectedClinicId(null);
    fetchAppointments(0, true);
    fetchClinics();

    toast({
      title: "Reloaded",
      description: "Data has been refreshed successfully!",
    });
  }, [state.resetFilterState, state.setSelectedClinicId, fetchAppointments, fetchClinics, toast]);

  // Handle reset filters - now restores original data
  const handleResetFilters = useCallback(() => {
    // Reset filter-related state
    state.setSelectedClinic("");
    state.setSelectedClinicId(null);
    state.setSelectedFacility("");
    state.setSelectedTimeSlot("");
    state.setFacilities([]);
    state.setFacilitySearchQuery("");
    state.setFilterClinicSearchQuery("");
    state.setIsFilterApplied(false);
    state.setCurrentPage(1);
    state.setFilterStartDate(undefined);
    state.setFilterEndDate(undefined);
    state.setFilterStartTime("");
    state.setFilterEndTime("");

    // Restore original unfiltered data if available, otherwise fetch fresh data
    if (state.originalAppointments.length > 0) {
      state.setAppointments(state.originalAppointments);
      toast({
        title: "Filters Reset",
        description: "Restored to original unfiltered data!",
      });
    } else {
      // Fallback: reload appointments to get all data without any filters
      fetchAppointments(0, true, false);
      toast({
        title: "Filters Reset",
        description: "All filters have been cleared successfully!",
      });
    }
  }, [state.originalAppointments, state.setSelectedClinic, state.setSelectedClinicId, state.setSelectedFacility, state.setSelectedTimeSlot, state.setFacilities, state.setFacilitySearchQuery, state.setFilterClinicSearchQuery, state.setIsFilterApplied, state.setCurrentPage, state.setFilterStartDate, state.setFilterEndDate, state.setFilterStartTime, state.setFilterEndTime, state.setAppointments, fetchAppointments, toast]);

  // Create appointment slots
  const handleConfirmSlot = useCallback(async () => {
    // Prevent multiple submissions
    if (state.isCreatingSlots) {
      return;
    }

    const formData = {
      dialogClinic: state.dialogClinic,
      selectedDialogFacility: state.selectedDialogFacility,
      selectedDays: state.selectedDays,
      startDate: state.startDate,
      endDate: state.endDate,
      startTime: state.startTime,
      endTime: state.endTime,
      duration: state.duration
    };

    const errors = appointmentSlotsService.validateSlotForm(formData);
    state.setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly.",
        variant: "destructive",
      });
      return;
    }

    if (!token || !user?.id) {
      toast({
        title: "Error",
        description: "User authentication required",
        variant: "destructive",
      });
      return;
    }

    state.setIsCreatingSlots(true);
    try {
      const clinic = state.clinics.find(c => c.clinic_name === state.dialogClinic);
      if (!clinic) {
        throw new Error("Selected clinic not found");
      }

      const facility = state.dialogFacilities.find(f => f.facility_name === state.selectedDialogFacility);
      if (!facility) {
        throw new Error("Selected facility not found");
      }

      const appointmentData = {
        appointment_dates: appointmentSlotsService.generateAppointmentDates(
          state.startDate!,
          state.endDate!,
          state.selectedDays
        ),
        starting_time: state.startTime + ":00",
        ending_time: state.endTime + ":00",
        duration: parseInt(state.duration)
      };

      const result = await appointmentSlotsService.createAppointmentSlots(
        token!,
        user.id,
        facility.facility_id,
        appointmentData
      );

      toast({
        title: "Success",
        description: result.message || "Time slots created successfully!",
      });

      // Reset dialog state
      state.setIsDialogOpen(false);
      state.resetDialogState();
      state.setIsCreatingSlots(false);

      // Refresh appointments data while maintaining filters
      handleRefreshData();
    } catch (error) {
      console.error("Error creating time slots:", error);
      state.setIsCreatingSlots(false);
      toast({
        title: "Error",
        description: "Failed to create time slots",
        variant: "destructive",
      });
    }
  }, [token, user, state.clinics, state.dialogFacilities, state.dialogClinic, state.selectedDialogFacility, state.selectedDays, state.startDate, state.endDate, state.startTime, state.endTime, state.duration, state.isCreatingSlots, state.setValidationErrors, state.setIsDialogOpen, state.setIsCreatingSlots, state.resetDialogState, toast, handleRefreshData]);

  // Delete appointment
  const handleDeleteAppointment = useCallback((appointmentId: number) => {
    state.setDeleteConfirmDialog({
      isOpen: true,
      appointmentId: appointmentId,
    });
  }, [state.setDeleteConfirmDialog]);

  const confirmDeleteAppointment = useCallback(async () => {
    if (!state.deleteConfirmDialog.appointmentId || !token || !user?.id) {
      return;
    }

    try {
      await appointmentSlotsService.deleteAppointment(token!, state.deleteConfirmDialog.appointmentId, user.id);

      toast({
        title: "Success",
        description: "Appointment deleted successfully!",
      });

      // Refresh appointments data while maintaining filters
      handleRefreshData();
    } catch (error) {
      console.error("Error deleting appointment:", error);
      toast({
        title: "Error",
        description: "Failed to delete appointment",
        variant: "destructive",
      });
    } finally {
      state.setDeleteConfirmDialog({ isOpen: false, appointmentId: null });
    }
  }, [token, user, state.deleteConfirmDialog, state.setDeleteConfirmDialog, toast, handleRefreshData]);

  // Delete all appointments - now accepts filtered appointments
  const handleDeleteAll = useCallback((filteredAppointments: any[]) => {
    // Store filtered appointments for deletion
    state.setFilteredAppointmentsForDeletion(filteredAppointments);
    state.setDeleteAllConfirmDialog(true);
  }, [state.setDeleteAllConfirmDialog, state.setFilteredAppointmentsForDeletion]);

  const confirmDeleteAll = useCallback(async () => {
    if (!token || !user?.id) {
      toast({
        title: "Error",
        description: "User authentication required",
        variant: "destructive",
      });
      return;
    }

    // Use filtered appointments instead of all appointments
    const appointmentsSource = state.filteredAppointmentsForDeletion || state.appointments;

    // Filter appointments that don't have patient_clinic_data_id (null values only)
    const appointmentsToDelete = appointmentsSource
      .filter(appointment => appointment.patient_clinic_data_id === null)
      .map(appointment => appointment.appointment_id);

    if (appointmentsToDelete.length === 0) {
      toast({
        title: "No appointments to delete",
        description: "All filtered appointments have associated patient data.",
        variant: "destructive",
      });
      state.setDeleteAllConfirmDialog(false);
      return;
    }

    try {
      const result = await appointmentSlotsService.deleteAllAppointments(
        token!,
        user.id,
        appointmentsToDelete
      );

      toast({
        title: "Success",
        description: `${appointmentsToDelete.length} filtered appointments deleted successfully!`,
      });

      // Clear the stored filtered appointments
      state.setFilteredAppointmentsForDeletion(null);

      // Refresh appointments data while maintaining filters
      handleRefreshData();
    } catch (error) {
      console.error("Error deleting appointments:", error);
      toast({
        title: "Error",
        description: "Failed to delete appointments",
        variant: "destructive",
      });
    } finally {
      state.setDeleteAllConfirmDialog(false);
    }
  }, [token, user, state.appointments, state.filteredAppointmentsForDeletion, state.setDeleteAllConfirmDialog, state.setFilteredAppointmentsForDeletion, toast, handleRefreshData]);

  // Handle filter confirm - now uses API-based filtering
  const handleFilterConfirm = useCallback(async () => {
    if (!state.filterStartDate || !state.filterEndDate || !state.filterStartTime || !state.filterEndTime) {
      toast({
        title: "Validation Error",
        description: "Please fill in all filter fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Fetch filtered data from the API
      await fetchAppointments(0, true, true); // Use advanced filtering
      
      // Apply filtering logic - set filter as applied
      state.setIsFilterApplied(true);
      toast({
        title: "Success",
        description: "Time slot filter applied successfully!",
      });

      // Close filter dialog
      state.setIsFilterDialogOpen(false);
    } catch (error) {
      console.error("Error applying filters:", error);
      toast({
        title: "Error",
        description: "Failed to apply filters",
        variant: "destructive",
      });
    }
  }, [state.filterStartDate, state.filterEndDate, state.filterStartTime, state.filterEndTime, state.setIsFilterApplied, state.setIsFilterDialogOpen, fetchAppointments, toast]);

  return {
    // Operations
    fetchClinics,
    fetchAppointments,
    fetchFacilities,
    fetchDialogFacilities,
    handleClinicChange,
    handleFacilityChange,
    handleDialogClinicChange,
    handleRowsPerPageChange,
    handlePageChange,
    handleNextPage,
    handlePrevPage,
    handleSearch,
    handleRefreshData,
    handleReload,
    handleResetFilters,
    handleConfirmSlot,
    handleDeleteAppointment,
    confirmDeleteAppointment,
    handleDeleteAll,
    confirmDeleteAll,
    handleFilterConfirm,

    // State (re-exported for convenience)
    ...state,
  };
};